import React from "react"
import {
  TextArea,
  Input,
  FileInput,
  Checkbox,
  FormField,
  SelectEntries,
  Error,
} from "@bw/forms"
import axios from "axios"
import { Field } from "formik"
import * as yup from "yup"
import parse from "html-react-parser"
import { Select } from "../../forms/select/Select"

export const getVisibleFields = (fields, formik) => {
  return fields.filter(field => {
    if (!field.if && !field.if_any) {
      return true
    }

    if (field.if_any) {
      for (const target in field.if_any) {
        const parts = field.if_any[target].split(" ")
        const condition = parts.shift()
        const match = parts.join(" ")

        if (conditionIsMet(match, condition, formik.values[target])) {
          return true
        }
      }
    }

    if (field.if) {
      for (const target in field.if) {
        const parts = field.if[target].split(" ")
        const condition = parts.shift()
        const match = parts.join(" ")

        if (!conditionIsMet(match, condition, formik.values[target])) {
          return false
        }
      }

      return true
    }

    return false
  })
}

const conditionIsMet = (match, condition, value) => {
  switch (condition) {
    case "equals":
      return match === value

    default:
      return false
  }
}

const privacyPolicyText =
  "politique d'échanges électroniques et de protection des données"
const privacyPolicyUrl =
  "https://www.mdscharmilles.ch/politique-de-communication-et-de-confidentialite/"

const requiredAsterisk = (
  <span style={{ color: "red", marginLeft: "0.3em" }}>*</span>
)

const decodeLabelText = text => {
  return text
    .replace(/&amp;/g, "&")
    .replace(/&#039;|&apos;|&rsquo;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&nbsp;/g, " ")
}

const renderCheckboxFallbackTitle = (field, title, required) => {
  const displayText =
    typeof field.display === "string"
      ? decodeLabelText(field.display.replace(/<[^>]*>/g, ""))
      : ""
  const linkIndex = displayText.indexOf(privacyPolicyText)

  if (linkIndex === -1) {
    return (
      <>
        {title}
        {required && requiredAsterisk}
      </>
    )
  }

  return (
    <>
      {displayText.slice(0, linkIndex)}
      <a
        href={privacyPolicyUrl}
        style={{ color: "var(--primary)", textDecoration: "none" }}
      >
        {privacyPolicyText}
      </a>
      {displayText.slice(linkIndex + privacyPolicyText.length)}
      {required && requiredAsterisk}
    </>
  )
}

export const displayField = (field, formik) => {
  const required = field?.validate?.join(" ")?.includes("required")
  const title = field.display ? parse(field.display) : undefined
  const checkboxOptions = Object.entries(field.options || {})

  switch (field.type) {
    case "textarea":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            placeholder={field.placeholder}
            name={field.handle}
            component={TextArea}
          />
        </FormField>
      )

    case "checkboxes":
      if (checkboxOptions.length === 0) {
        return (
          <div style={{ marginBottom: "20px" }}>
            {field.instructions && field.instructions_position === "above" && (
              <small>{field.instructions}</small>
            )}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <Field
                type="checkbox"
                name={field.handle}
                value="1"
                style={{
                  width: "20px",
                  height: "20px",
                  flex: "0 0 20px",
                  marginTop: "3px",
                  accentColor: "var(--primary)",
                  cursor: "pointer",
                }}
              />
              <span>
                {renderCheckboxFallbackTitle(field, title, false)}
              </span>
            </div>
            {field.instructions && field.instructions_position === "below" && (
              <small>{field.instructions}</small>
            )}
            <Error name={field.handle} />
          </div>
        )
      }

      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          {checkboxOptions.map(([value, label]) => {
            return (
              <label
                key={value}
                style={{ display: "flex", gap: "5px", alignItems: "center" }}
              >
                <Field component={Checkbox} name={field.handle} value={value} />
                {label || value}
              </label>
            )
          })}
        </FormField>
      )

    case "toggle":
      return (
        <FormField
          name={field.handle}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <label style={{ display: "flex", gap: "5px", alignItems: "start" }}>
            <Field
              component={Checkbox}
              type="checkbox"
              name={field.handle}
              style={{ marginTop: "1px" }}
            />
            <span>{field?.display ? parse(field.display) : null}</span>
          </label>
        </FormField>
      )

    case "radio":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          {Object.values(field.options || {}).map((label, i) => {
            const value = Object.keys(field.options || {})[i]
            return (
              <label
                key={value}
                style={{ display: "flex", gap: "5px", alignItems: "center" }}
              >
                <Field type="radio" name={field.handle} value={value} />
                {label || value}
              </label>
            )
          })}
        </FormField>
      )

    case "select":
      const options = Object.entries(field.options || {}).map(([value, label]) => ({
        value,
        label,
      }))

      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field component={Select} name={field.handle} options={options} />
        </FormField>
      )

    case "entries":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            component={SelectEntries}
            name={field.handle}
            collections={field.collections}
            isMulti={true}
            isOptionDisabled={() => {
              return formik.values[field.handle].length >= field.max_items
            }}
          />
        </FormField>
      )

    case "assets":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            name="file"
            onChange={event => {
              formik.setFieldValue(field.handle, [...event.currentTarget.files])
            }}
            component={FileInput}
          />
        </FormField>
      )

    case "integer":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            type="number"
            step="1"
            placeholder={field.placeholder}
            name={field.handle}
            component={Input}
          />
        </FormField>
      )

    case "text":
    case "number":
    default:
      if (field.handle === "page_title") {
        field.input_type = "hidden"
      }
      return field.input_type === "hidden" ? (
        <Field
          type={field.input_type}
          name={field.handle}
          component={Input}
          value={resolvePlaceholder(field.placeholder)}
        />
      ) : (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            type={field.input_type}
            placeholder={field.placeholder}
            name={field.handle}
            component={Input}
          />
        </FormField>
      )
  }
}

const resolvePlaceholder = placeholder => {
  switch (placeholder) {
    case "title":
      return document.title
    default:
      return undefined
  }
}

const normalizeFormUrl = url => {
  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    url?.startsWith("http://")
  ) {
    return url.replace("http://", "https://")
  }

  return url
}

const getUrlOrigin = url => {
  if (!url) {
    return ""
  }

  try {
    return new URL(normalizeFormUrl(url)).origin
  } catch (e) {
    return ""
  }
}

const getFormBaseUrl = form => {
  return getUrlOrigin(form?.api_url) || getUrlOrigin(process.env.GATSBY_API_URL)
}

const getFormCsrfCookie = form => {
  const baseUrl = getFormBaseUrl(form)

  if (!baseUrl) {
    return Promise.resolve()
  }

  return axios({
    method: "GET",
    url: normalizeFormUrl(`${baseUrl}/sanctum/csrf-cookie`),
    timeout: 10000,
  })
}

export const getFormFields = form => {
  const baseUrl = getFormBaseUrl(form)
  const url = form?.api_url
    ? normalizeFormUrl(form.api_url)
    : baseUrl
    ? normalizeFormUrl(`${baseUrl}/api/forms/${form?.handle}`)
    : null

  if ((!form?.handle && !form?.api_url) || !url) {
    return Promise.reject(new Error("Missing form handle"))
  }

  return getFormCsrfCookie(form)
    .catch(() => null)
    .then(() =>
      axios({
        method: "GET",
        url,
        timeout: 10000,
      })
    )
    .then(response => {
      return response.data.data.fields
    })
}

export const postFormFields = (form, values) => {
  const baseUrl = getFormBaseUrl(form)

  if (!baseUrl || !form?.handle) {
    return Promise.reject(new Error("Missing form handle"))
  }

  const dataToSend = new FormData()
  dataToSend.append("_method", "POST")
  Object.entries(values).forEach(([key, value]) => {
    if (typeof value === "object" && value.length > 0) {
      value.forEach(file => {
        dataToSend.append(`${key}[]`, file)
      })
    } else {
      dataToSend.append(key, value)
    }
  })

  return getFormCsrfCookie(form).then(() =>
    axios({
      method: "POST",
      url: normalizeFormUrl(`${baseUrl}/!/forms/${form.handle}`),
      data: dataToSend,
      timeout: 10000,
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Requested-With": "XMLHttpRequest",
      },
    }).then(response => {
      if (response.status !== 200) {
        throw new Error("Invalid server response")
      }
      return response
    })
  )
}

const validateIf = (values, conditions) => {
  for (let i = 0; i < values.length; i++) {
    const value = values[i]
    const parts = conditions[i].split(" ")
    const condition = parts.shift()
    const match = parts.join(" ")

    if (!conditionIsMet(match, condition, value)) {
      return false
    }
  }

  return true
}

const validateIfAny = (values, conditions) => {
  for (let i = 0; i < values.length; i++) {
    const value = values[i]
    const parts = conditions[i].split(" ")
    const condition = parts.shift()
    const match = parts.join(" ")

    if (conditionIsMet(match, condition, value)) {
      return true
    }
  }

  return false
}

const getRequiredFieldValidator = field => {
  let dependencies
  let validator

  if (field.if) {
    dependencies = Object.keys(field.if)
    validator = values => validateIf(values, Object.values(field.if))
  }

  if (field.if_any) {
    dependencies = Object.keys(field.if_any)
    validator = values => validateIfAny(values, Object.values(field.ifAny))
  }

  return [dependencies, validator]
}

export const getInitialValues = fields => {
  return Object.values(fields).reduce((acc, field) => {
    switch (field.type) {
      case "toggle":
        acc[field.handle] = false
        break

      case "entries":
        acc[field.handle] = []
        break

      case "checkboxes":
        acc[field.handle] = []
        break

      case "select":
      case "radio":
        acc[field.handle] = Object.keys(field.options || {})[0] || ""
        break

      case "text":
      case "textarea":
      default:
        acc[field.handle] = field.default || ""

        if (field.handle === "page_title") {
          acc[field.handle] = document.querySelector("h1").textContent
        }

        break
    }
    return acc
  }, {})
}

const fieldType = type => {
  switch (type) {
    case "text":
    case "textarea":
    case "radio":
    case "select":
      return "string"

    case "toggle":
      return "bool"

    case "number":
    case "integer":
      return "number"

    case "email":
      return "email"

    case "checkboxes":
      return "array"

    case "assets":
    default:
      return "mixed"
  }
}

const getTranslatedMessage = (t, key, fallback) => {
  const translated = t(key)

  return translated && translated !== key ? translated : fallback
}

export const fieldsSchema = (fields, t) => {
  const requiredMessage = getTranslatedMessage(
    t,
    "form.required",
    "Veuillez renseigner ce champ obligatoire."
  )
  const integerMessage = getTranslatedMessage(
    t,
    "form.mustBeInteger",
    "Veuillez saisir un nombre entier."
  )

  return Object.values(fields).reduce((acc, field) => {
    const yupType = fieldType(field.type)
    let yupField = yup[yupType]()

    if (field.validate && field.validate.join(" ").includes("required")) {
      if (!field.if && !field.if_any) {
        switch (yupType) {
          case "bool":
            yupField = yupField.oneOf([true], requiredMessage)
            break

          case "array":
            yupField = yupField.min(1, requiredMessage).of(yup.string())
            break

          default:
            yupField = yupField.required(requiredMessage)
        }
      } else {
        const [dependencies, validator] = getRequiredFieldValidator(field)
        switch (yupType) {
          case "bool":
            yupField = yupField.when(dependencies, {
              is: (...params) => validator(params),
              then: schema => schema.oneOf([true], requiredMessage),
            })
            break

          case "array":
            yupField = yupField.when(dependencies, {
              is: (...params) => validator(params),
              then: schema =>
                schema.min(1, requiredMessage).of(yup.string()),
            })
            break

          default:
            yupField = yupField.when(dependencies, {
              is: (...params) => validator(params),
              then: schema => schema.required(requiredMessage),
            })
        }
      }
    } else {
      yupField = yupField.nullable()
    }

    if (field.type === "integer") {
      yupField = yupField.integer(integerMessage)
    }

    acc[field.handle] = yupField
    return acc
  }, {})
}
